import { create } from 'zustand';

/**
 * Toast system (web-standard name). Bottom-right stack with auto-dismiss,
 * click-to-dismiss, and `Esc` to dismiss the most-recent toast.
 *
 * Toast is intentionally NOT persisted: it is an ephemeral channel for
 * "you just did a thing" feedback. Persisting it would surface stale messages
 * after a hard reload, which is the exact bug the per-page-mount lifecycle
 * is designed to avoid.
 */

export type ToastKind = 'info' | 'success' | 'warning' | 'error';

export interface Toast {
  readonly id: string;
  readonly title: string;
  readonly body?: string;
  readonly kind: ToastKind;
  /** Milliseconds until auto-dismiss. `0` means sticky (no auto-dismiss). */
  readonly durationMs: number;
}

interface ToastStore {
  toasts: Toast[];
  /** Add a toast to the top of the stack. Returns the assigned id. */
  push(toast: Omit<Toast, 'id'> & { id?: string }): string;
  /** Dismiss a specific toast by id. Safe to call on a non-existent id. */
  dismiss(id: string): void;
  /** Dismiss the most-recent toast. Wired to the `Esc` key in ToastHost. */
  dismissTop(): void;
  /** Dismiss every toast (used on screen transitions if needed). */
  clear(): void;
}

let counter = 0;
const nextId = (): string => {
  counter += 1;
  return `t-${Date.now().toString(36)}-${counter.toString(36)}`;
};

// Under AUDIT_CAPTURE the toast auto-dismiss timer races the screenshot:
// a capture taken after the dismissal shows one fewer toast than one taken
// before it, and the golden flips between the two states run to run (the
// tablet-quirks overlay capture did exactly this). Sticky toasts make the
// captured state deterministic — every toast pushed is in the frame. Same
// env gate and rationale as the reduce-motion switch in main.tsx.
const CAPTURE_STICKY_TOASTS = import.meta.env.AUDIT_CAPTURE === '1';

export const useToastStore = create<ToastStore>()((set, get) => ({
  toasts: [],
  push(toast) {
    const id = toast.id ?? nextId();
    const full: Toast = {
      id,
      title: toast.title,
      body: toast.body,
      kind: toast.kind,
      durationMs: CAPTURE_STICKY_TOASTS ? 0 : toast.durationMs,
    };
    // Newest-on-top: prepend so the most recent toast is visually at the top
    // of the bottom-right stack (matches the user's reading direction).
    set({ toasts: [full, ...get().toasts] });
    return id;
  },
  dismiss(id) {
    set({ toasts: get().toasts.filter((t) => t.id !== id) });
  },
  dismissTop() {
    const top = get().toasts[0];
    if (top) get().dismiss(top.id);
  },
  clear() {
    set({ toasts: [] });
  },
}));
